/**
 * `/<tenantSlug>/admin/playground` — Admin Lab: Conversation Playground.
 *
 * Per ADR-046 Phase 1: free-form Concierge chat surface with a side
 * pane that shows the full reasoning trace for the most recent turn
 * (tool dispatches, model token counts, the linked L3 memory_id). This
 * is the UI affordance that lets an engineer verify L4's memory-context
 * wiring (ADR-048) without `psql` queries.
 *
 * Wire:
 *   POST /api/shell/concierge/messages  — same dispatch as the dock
 *   SSE  concierge.message.delta         — listen for the assistant reply
 *   GET  /api/shell/admin/runs/{id}/trace — fetch the per-turn trace
 *
 * Permission gate: `admin.playground.view`. Owner role gets this by
 * default. Failures from the trace endpoint surface inline; failures
 * from the dispatch POST surface in the error banner.
 */

import { useApiClient } from "@ken/api-client";
import { useSSE } from "@ken/sse";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
} from "@ken/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Send } from "lucide-react";
import {
  useCallback,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";

export const Route = createFileRoute("/$tenantSlug/admin/playground")({
  component: PlaygroundRoute,
});

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

interface ConciergeDelta {
  message_id: string;
  client_message_id: string;
  role: "assistant";
  body: string;
  kind: "complete" | "not_configured" | "error";
  run_id: string | null;
}

interface RunSummary {
  run_id: string;
  started_at: string;
  ended_at: string | null;
  agent_type: string;
  status: string;
  outcome: string | null;
  user_message: string | null;
  final_output: string | null;
  model_id: string;
  token_input_count: number;
  token_output_count: number;
  l3_memory_id: string | null;
  conversation_id: string | null;
}

interface ToolCall {
  call_sequence: number;
  call_id: string;
  tool_name: string;
  arguments: unknown;
  result: unknown;
  status: string;
  error_message: string | null;
  started_at: string;
  ended_at: string | null;
}

interface RunTraceResponse {
  run: RunSummary;
  tool_calls: ToolCall[];
}

interface ApiClientLike {
  POST: (
    path: string,
    init: {
      body: {
        body: string;
        client_message_id: string;
        conversation_id: string;
      };
    },
  ) => Promise<{ response: Response }>;
  GET: (path: string) => Promise<{ data?: unknown; response: Response }>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Bubble {
  id: string;
  role: "user" | "assistant" | "error";
  body: string;
  /** Only set on assistant bubbles — used by the trace pane. */
  run_id?: string | null;
  /** Set on the user bubble that produced this turn; used to match the SSE delta. */
  client_message_id?: string;
}

function PlaygroundRoute(): ReactElement {
  const apiClient = useApiClient();
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [latestRunId, setLatestRunId] = useState<string | null>(null);

  // One conversation_id for the lifetime of this route mount.
  const conversationIdRef = useRef<string>(crypto.randomUUID());

  // ---- SSE: capture replies for THIS playground only -------------------
  // The dock + the playground both share `concierge.message.delta` since
  // the SSE registry emits per user. We filter by client_message_id —
  // only replies for messages WE sent flow into this state.
  const pendingClientMsgIds = useRef<Set<string>>(new Set());

  useSSE<ConciergeDelta>("concierge.message.delta", {
    onMessage: (delta) => {
      if (!pendingClientMsgIds.current.has(delta.client_message_id)) return;
      pendingClientMsgIds.current.delete(delta.client_message_id);
      setBubbles((prev) => [
        ...prev,
        {
          id: delta.message_id,
          role: delta.kind === "error" ? "error" : "assistant",
          body: delta.body,
          run_id: delta.run_id,
        },
      ]);
      if (delta.run_id) setLatestRunId(delta.run_id);
    },
  });

  const traceQuery = useQuery({
    queryKey: ["admin", "trace", latestRunId],
    queryFn: async () => {
      const r = await (apiClient as unknown as ApiClientLike).GET(
        `/api/shell/admin/runs/${encodeURIComponent(latestRunId!)}/trace`,
      );
      if (!r.response.ok) throw new Error(`trace ${r.response.status}`);
      return r.data as RunTraceResponse;
    },
    enabled: latestRunId !== null,
  });

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      const body = draft.trim();
      if (!body) return;
      setDraft("");
      setError(null);

      const clientId = crypto.randomUUID();
      const userBubbleId = crypto.randomUUID();
      pendingClientMsgIds.current.add(clientId);
      setBubbles((prev) => [
        ...prev,
        { id: userBubbleId, role: "user", body, client_message_id: clientId },
      ]);

      try {
        const res = await (apiClient as unknown as ApiClientLike).POST(
          "/api/shell/concierge/messages",
          {
            body: {
              body,
              client_message_id: clientId,
              conversation_id: conversationIdRef.current,
            },
          },
        );
        if (!res.response.ok) {
          throw new Error(`server responded ${res.response.status}`);
        }
      } catch (err) {
        pendingClientMsgIds.current.delete(clientId);
        setError(err instanceof Error ? err.message : "Send failed");
      }
    },
    [apiClient, draft],
  );

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="admin-playground-page">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
          Admin Lab
        </div>
        <h1 className="text-2xl font-semibold mt-1">Conversation Playground</h1>
        <p className="text-sm text-[var(--color-text-soft)] mt-1">
          Free-form Concierge testing. Every turn shows its reasoning
          trace beside it — tool dispatches, model tokens, and the
          recalled memory id. Use this to verify wiring before checking
          the deployed dock.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
        {/* ---- Chat pane ---------------------------------------- */}
        <Card data-testid="admin-playground-chat">
          <CardHeader>
            <CardTitle className="text-base">Chat</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              data-testid="admin-playground-bubbles"
              className="flex flex-col gap-2 max-h-[560px] overflow-y-auto pr-1"
            >
              {bubbles.length === 0 && (
                <div className="text-sm text-[var(--color-text-faint)] py-8 text-center">
                  Ask Ken anything — for example, &ldquo;list customers&rdquo;.
                </div>
              )}
              {bubbles.map((b) => (
                <div
                  key={b.id}
                  data-testid={`admin-playground-bubble-${b.role}-${b.id}`}
                  className={[
                    "max-w-[85%] px-4 py-2 rounded-[var(--radius-md)] text-sm whitespace-pre-wrap",
                    b.role === "user"
                      ? "self-end bg-[oklch(94%_0.012_240)]"
                      : b.role === "error"
                        ? "self-start bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)] text-[var(--color-accent-red)]"
                        : "self-start bg-white border border-[var(--color-glass-border)]",
                  ].join(" ")}
                >
                  {b.body}
                </div>
              ))}
              {error !== null && (
                <div
                  role="alert"
                  className="text-xs text-[var(--color-accent-red)] mt-1"
                >
                  Send failed: {error}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => void handleSubmit(e)}
              className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--color-glass-border)]"
            >
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Ask Ken about your business…"
                data-testid="admin-playground-input"
                aria-label="Playground chat input"
                className="flex-1 h-10 px-4 rounded-full bg-white border border-[oklch(0%_0_0_/_0.08)] text-sm placeholder:text-[oklch(58%_0.015_240)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(45%_0.08_240)]/30"
              />
              <button
                type="submit"
                aria-label="Send message"
                data-testid="admin-playground-submit"
                disabled={!draft.trim()}
                className="grid place-items-center w-10 h-10 rounded-full bg-[oklch(25%_0.06_240)] text-white shadow-[0_4px_12px_-3px_oklch(20%_0.06_230_/_0.4)] disabled:opacity-50 disabled:pointer-events-none"
              >
                <Send size={16} />
              </button>
            </form>
          </CardContent>
        </Card>

        {/* ---- Reasoning trace pane ---------------------------- */}
        <Card data-testid="admin-playground-trace">
          <CardHeader>
            <CardTitle className="text-base">Reasoning Trace</CardTitle>
          </CardHeader>
          <CardContent>
            {latestRunId === null ? (
              <div className="text-sm text-[var(--color-text-faint)]">
                Send a message to see its trace.
              </div>
            ) : traceQuery.isLoading ? (
              <div className="text-sm">Loading trace…</div>
            ) : traceQuery.isError ? (
              <div className="text-sm text-[var(--color-accent-red)]" role="alert">
                Failed to load: {String(traceQuery.error)}
              </div>
            ) : traceQuery.data ? (
              <PlaygroundTrace trace={traceQuery.data} />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PlaygroundTrace({ trace }: { trace: RunTraceResponse }): ReactElement {
  const { run, tool_calls } = trace;
  return (
    <div className="flex flex-col gap-3 text-xs" data-testid="admin-playground-trace-body">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Run id" value={`${run.run_id.slice(0, 8)}…`} mono />
        <Field
          label="Status"
          value={<Chip tone={run.status === "completed" ? "green" : "amber"}>{run.status}</Chip>}
        />
        <Field label="Model" value={run.model_id} mono />
        <Field
          label="Tokens (in / out)"
          value={`${run.token_input_count} / ${run.token_output_count}`}
          mono
        />
        <Field
          label="Conversation"
          value={run.conversation_id ? `${run.conversation_id.slice(0, 8)}…` : "—"}
          mono
        />
        <Field
          label="L3 memory id"
          value={run.l3_memory_id ? `${run.l3_memory_id.slice(0, 8)}…` : "—"}
          mono
        />
      </div>

      {tool_calls.length > 0 ? (
        <section>
          <div className="uppercase tracking-wide text-[var(--color-text-faint)] mb-1">
            Tool calls ({tool_calls.length})
          </div>
          <div className="flex flex-col gap-2">
            {tool_calls.map((tc) => (
              <div
                key={tc.call_id}
                data-testid={`admin-playground-trace-tool-${tc.call_sequence}`}
                className="p-2 rounded border border-[var(--color-glass-border)] bg-white"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Chip tone={tc.status === "completed" ? "green" : "amber"}>
                    {tc.status}
                  </Chip>
                  <code className="font-semibold">{tc.tool_name}</code>
                </div>
                <details>
                  <summary className="cursor-pointer">arguments</summary>
                  <pre className="mt-1 p-2 rounded bg-[oklch(98%_0.003_230_/_0.7)] overflow-x-auto">
                    {JSON.stringify(tc.arguments, null, 2)}
                  </pre>
                </details>
                <details className="mt-1">
                  <summary className="cursor-pointer">result</summary>
                  <pre className="mt-1 p-2 rounded bg-[oklch(98%_0.003_230_/_0.7)] overflow-x-auto max-h-48">
                    {JSON.stringify(tc.result, null, 2)}
                  </pre>
                </details>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <div className="text-[var(--color-text-faint)]">No tool calls in this turn.</div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactElement | string;
  mono?: boolean;
}): ReactElement {
  return (
    <div>
      <div className="uppercase tracking-wide text-[var(--color-text-faint)]">
        {label}
      </div>
      <div className={mono ? "font-mono" : ""}>{value}</div>
    </div>
  );
}
